import React, { useState } from 'react';
import { SparklesIcon, ZapIcon, Share2Icon, ChefHatIcon, ImageIcon, LanguagesIcon, ArrowRightIcon, CheckIcon, UsersIcon, HeartIcon, ChevronLeftIcon, ChevronRightIcon, CodeIcon, BrainIcon, PaletteIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export const About = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const exampleApps = [{
    icon: ImageIcon,
    name: 'Ghiblifi',
    description: 'Turn your photos into magical Studio Ghibli art with AI-powered style transfer',
    color: 'from-green-400 to-emerald-500',
    emoji: '🎨',
    screenshot: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=800&h=600&fit=crop'
  }, {
    icon: ChefHatIcon,
    name: 'Cooking Helper',
    description: 'Get personalized recipes based on what you have in your kitchen',
    color: 'from-orange-400 to-red-500',
    emoji: '👨‍🍳',
    screenshot: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800&h=600&fit=crop'
  }, {
    icon: LanguagesIcon,
    name: 'Lingua Translate',
    description: 'Translate anything with context-aware AI and tone control',
    color: 'from-blue-400 to-cyan-500',
    emoji: '🌍',
    screenshot: 'https://images.unsplash.com/photo-1523289333742-be1143f6b766?w=800&h=600&fit=crop'
  }, {
    icon: CodeIcon,
    name: 'CodeCraft AI',
    description: 'Optimize, debug, and explain code in any programming language',
    color: 'from-purple-400 to-indigo-500',
    emoji: '💻',
    screenshot: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&h=600&fit=crop'
  }, {
    icon: BrainIcon,
    name: 'ELI5 Explainer',
    description: "Understand complex topics explained like you're five years old",
    color: 'from-pink-400 to-rose-500',
    emoji: '🧠',
    screenshot: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=600&fit=crop'
  }, {
    icon: PaletteIcon,
    name: 'Image Wizard Pro',
    description: 'Generate stunning images from text descriptions with AI',
    color: 'from-violet-400 to-purple-500',
    emoji: '✨',
    screenshot: 'https://images.unsplash.com/photo-1547826039-bfc35e0f1ea8?w=800&h=600&fit=crop'
  }];
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % exampleApps.length);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + exampleApps.length) % exampleApps.length);
  };
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };
  return <div className="min-h-screen bg-white" data-id="element-383">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50" data-id="element-384">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] bg-[size:40px_40px]" data-id="element-385" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-id="element-386">
          <div className="text-center" data-id="element-387">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-indigo-200 mb-6" data-id="element-388">
              <SparklesIcon className="h-4 w-4 text-indigo-600" data-id="element-389" />
              <span className="text-sm font-medium text-indigo-600" data-id="element-390">
                No code required
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6" data-id="element-391">
              Create AI Apps
              <br data-id="element-392" />
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-393">
                In Minutes, Not Months
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto" data-id="element-394">
              Turn your ideas into real AI apps. No coding, no setup, no hassle.
              Just describe what you want and watch it come to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-395">
              <button onClick={() => navigate('/create')} className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center" data-id="element-396">
                Start Creating Free
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-397" />
              </button>
              <button onClick={() => navigate('/')} className="bg-white text-gray-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all border-2 border-gray-200 inline-flex items-center justify-center" data-id="element-398">
                Explore Apps
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Carousel Section */}
      <div className="py-20 bg-white" data-id="element-399">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-400">
          <div className="text-center mb-16" data-id="element-401">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-402">
              See What People Are Creating
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-403">
              From fun photo transformers to practical productivity tools, the
              possibilities are endless
            </p>
          </div>

          {/* Carousel */}
          <div className="relative max-w-5xl mx-auto" data-id="element-404">
            {/* Main Slide */}
            <div className="relative overflow-hidden rounded-3xl" data-id="element-405">
              <div className="flex transition-transform duration-500 ease-out" style={{
              transform: `translateX(-${currentSlide * 100}%)`
            }} data-id="element-406">
                {exampleApps.map((app, index) => <div key={index} className="min-w-full" data-id="element-407">
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl border-2 border-gray-200 overflow-hidden shadow-xl" data-id="element-408">
                      <div className="grid md:grid-cols-2 gap-8 p-8 md:p-12" data-id="element-409">
                        {/* Content */}
                        <div className="flex flex-col justify-center" data-id="element-410">
                          <div className="text-6xl mb-6" data-id="element-411">{app.emoji}</div>
                          <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-412">
                            {app.name}
                          </h3>
                          <p className="text-lg text-gray-600 mb-8" data-id="element-413">
                            {app.description}
                          </p>
                          <button onClick={() => navigate('/')} className={`inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r ${app.color} text-white rounded-xl font-semibold hover:shadow-lg transition-all`} data-id="element-414">
                            Try it out
                            <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-415" />
                          </button>
                        </div>

                        {/* Screenshot */}
                        <div className="relative" data-id="element-416">
                          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white" data-id="element-417">
                            <img src={app.screenshot} alt={app.name} className="w-full h-full object-cover" data-id="element-418" />
                            <div className={`absolute inset-0 bg-gradient-to-br ${app.color} opacity-20`} data-id="element-419"></div>
                          </div>
                          {/* Decorative elements */}
                          <div className={`absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br ${app.color} rounded-full blur-2xl opacity-30`} data-id="element-420"></div>
                          <div className={`absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-br ${app.color} rounded-full blur-2xl opacity-30`} data-id="element-421"></div>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 bg-white rounded-full shadow-lg border-2 border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all z-10" aria-label="Previous slide" data-id="element-422">
              <ChevronLeftIcon className="h-6 w-6 text-gray-600" data-id="element-423" />
            </button>
            <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 bg-white rounded-full shadow-lg border-2 border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all z-10" aria-label="Next slide" data-id="element-424">
              <ChevronRightIcon className="h-6 w-6 text-gray-600" data-id="element-425" />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center space-x-2 mt-8" data-id="element-426">
              {exampleApps.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`transition-all ${currentSlide === index ? 'w-8 h-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full' : 'w-3 h-3 bg-gray-300 rounded-full hover:bg-gray-400'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-427" />)}
            </div>
          </div>
        </div>
      </div>

      {/* How It Works - Simple Graphic */}
      <div className="py-20 bg-gradient-to-br from-gray-50 to-white" data-id="element-428">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-429">
          <div className="text-center mb-16" data-id="element-430">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-431">
              It's Really This Simple
            </h2>
            <p className="text-lg text-gray-600" data-id="element-432">
              Three steps to your own AI app
            </p>
          </div>

          <div className="max-w-4xl mx-auto" data-id="element-433">
            <div className="relative" data-id="element-434">
              {/* Connection Lines */}
              <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-indigo-200 via-purple-200 to-pink-200" data-id="element-435" />

              <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative" data-id="element-436">
                {/* Step 1 */}
                <div className="text-center" data-id="element-437">
                  <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg mb-6 transform hover:scale-110 transition-transform" data-id="element-438">
                    <span className="text-3xl" data-id="element-439">💬</span>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-sm font-bold text-indigo-600 shadow-md" data-id="element-440">
                      1
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-441">
                    Describe Your Idea
                  </h3>
                  <p className="text-gray-600" data-id="element-442">
                    Just tell us what you want to build in plain English
                  </p>
                </div>

                {/* Step 2 */}
                <div className="text-center" data-id="element-443">
                  <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg mb-6 transform hover:scale-110 transition-transform" data-id="element-444">
                    <span className="text-3xl" data-id="element-445">✨</span>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-sm font-bold text-purple-600 shadow-md" data-id="element-446">
                      2
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-447">
                    Watch It Build
                  </h3>
                  <p className="text-gray-600" data-id="element-448">
                    Our AI creates your app in real-time, no coding needed
                  </p>
                </div>

                {/* Step 3 */}
                <div className="text-center" data-id="element-449">
                  <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl shadow-lg mb-6 transform hover:scale-110 transition-transform" data-id="element-450">
                    <span className="text-3xl" data-id="element-451">🚀</span>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-sm font-bold text-pink-600 shadow-md" data-id="element-452">
                      3
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-453">
                    Share & Use
                  </h3>
                  <p className="text-gray-600" data-id="element-454">
                    Use it yourself or share with the world instantly
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white" data-id="element-455">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-456">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-457">
            <div data-id="element-458">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-459">
                Everything You Need,
                <br data-id="element-460" />
                Nothing You Don't
              </h2>
              <div className="space-y-6" data-id="element-461">
                <div className="flex items-start space-x-4" data-id="element-462">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center" data-id="element-463">
                    <CheckIcon className="h-6 w-6 text-green-600" data-id="element-464" />
                  </div>
                  <div data-id="element-465">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-466">
                      No Technical Skills Required
                    </h3>
                    <p className="text-gray-600" data-id="element-467">
                      If you can describe what you want, you can build it. No
                      coding, no design skills needed.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-id="element-468">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center" data-id="element-469">
                    <Share2Icon className="h-6 w-6 text-blue-600" data-id="element-470" />
                  </div>
                  <div data-id="element-471">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-472">
                      Easy Sharing & Publishing
                    </h3>
                    <p className="text-gray-600" data-id="element-473">
                      Share your apps with a link or publish to our store. It's
                      your choice.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-id="element-474">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center" data-id="element-475">
                    <ZapIcon className="h-6 w-6 text-purple-600" data-id="element-476" />
                  </div>
                  <div data-id="element-477">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-478">
                      Instant Updates
                    </h3>
                    <p className="text-gray-600" data-id="element-479">
                      Change your mind? Just describe what you want different
                      and watch it update.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4" data-id="element-480">
                  <div className="flex-shrink-0 w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center" data-id="element-481">
                    <UsersIcon className="h-6 w-6 text-pink-600" data-id="element-482" />
                  </div>
                  <div data-id="element-483">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-484">
                      Join a Creative Community
                    </h3>
                    <p className="text-gray-600" data-id="element-485">
                      Discover apps others have made, remix them, and share your
                      own creations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative" data-id="element-486">
              <div className="relative bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl p-8 shadow-2xl" data-id="element-487">
                <div className="space-y-4" data-id="element-488">
                  <div className="bg-white rounded-xl p-4 shadow-md transform -rotate-2 hover:rotate-0 transition-transform" data-id="element-489">
                    <div className="flex items-center space-x-3 mb-2" data-id="element-490">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center text-xl" data-id="element-491">
                        🎨
                      </div>
                      <div className="flex-1" data-id="element-492">
                        <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" data-id="element-493"></div>
                        <div className="h-2 bg-gray-100 rounded w-1/2" data-id="element-494"></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-4 shadow-md transform rotate-1 hover:rotate-0 transition-transform" data-id="element-495">
                    <div className="flex items-center space-x-3 mb-2" data-id="element-496">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center text-xl" data-id="element-497">
                        👨‍🍳
                      </div>
                      <div className="flex-1" data-id="element-498">
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" data-id="element-499"></div>
                        <div className="h-2 bg-gray-100 rounded w-3/4" data-id="element-500"></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-4 shadow-md transform -rotate-1 hover:rotate-0 transition-transform" data-id="element-501">
                    <div className="flex items-center space-x-3 mb-2" data-id="element-502">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-lg flex items-center justify-center text-xl" data-id="element-503">
                        🌍
                      </div>
                      <div className="flex-1" data-id="element-504">
                        <div className="h-3 bg-gray-200 rounded w-5/6 mb-2" data-id="element-505"></div>
                        <div className="h-2 bg-gray-100 rounded w-2/3" data-id="element-506"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-yellow-200 to-orange-200 rounded-full blur-3xl opacity-50" data-id="element-507"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full blur-3xl opacity-50" data-id="element-508"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof */}
      <div className="py-16 bg-gradient-to-br from-indigo-50 to-purple-50" data-id="element-509">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-510">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-511">
            <div data-id="element-512">
              <div className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2" data-id="element-513">
                50K+
              </div>
              <div className="text-gray-600 font-medium" data-id="element-514">Apps Created</div>
            </div>
            <div data-id="element-515">
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2" data-id="element-516">
                100K+
              </div>
              <div className="text-gray-600 font-medium" data-id="element-517">Happy Users</div>
            </div>
            <div data-id="element-518">
              <div className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent mb-2" data-id="element-519">
                2M+
              </div>
              <div className="text-gray-600 font-medium" data-id="element-520">Apps Used</div>
            </div>
            <div data-id="element-521">
              <div className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2" data-id="element-522">
                4.9★
              </div>
              <div className="text-gray-600 font-medium" data-id="element-523">User Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 py-20" data-id="element-524">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:40px_40px]" data-id="element-525" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-526">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-id="element-527">
            <HeartIcon className="h-4 w-4 text-white" data-id="element-528" />
            <span className="text-sm font-medium text-white" data-id="element-529">
              Join thousands of creators
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-530">
            Ready to Bring Your Ideas to Life?
          </h2>
          <p className="text-xl text-indigo-100 mb-10 max-w-2xl mx-auto" data-id="element-531">
            Start creating your first AI app today. No credit card required, no
            commitment.
          </p>
          <button onClick={() => navigate('/create')} className="bg-white text-indigo-600 px-10 py-5 rounded-xl text-lg font-bold hover:bg-indigo-50 transition-all shadow-2xl hover:shadow-3xl inline-flex items-center" data-id="element-532">
            Create Your First App Free
            <SparklesIcon className="ml-2 h-5 w-5" data-id="element-533" />
          </button>
        </div>
      </div>
    </div>;
};